import React, { FunctionComponent } from 'react';
import { I18nSupport } from '@/utils/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, AccordionHeader, AccordionSection, Col, Container, Row } from '@visalex/core';

import { TFunction } from 'i18next';
import { useTranslation } from 'next-i18next';

import styles from './ProgressAccordion.module.scss';

type AccordionLabelProps = I18nSupport & {
  index: number;
  t: TFunction;
};

type ProgressEntryProps = AccordionLabelProps &
  I18nSupport & {
    amountOfPros: number;
    amountOfCons: number;
  };

const ProgressEntry: FunctionComponent<ProgressEntryProps> = ({
  index,
  t,
  i18nPrefix,
  amountOfPros = 1,
  amountOfCons = 3,
}) => {
  const pros = [];
  const cons = [];

  for (let i = 0; i < Math.max(amountOfPros, amountOfCons); i++) {
    if (i < amountOfPros) pros.push(t(`${i18nPrefix}.intro.accordion.${index}.pros.${i}`));

    if (i < amountOfCons) cons.push(t(`${i18nPrefix}.intro.accordion.${index}.cons.${i}`));
  }

  return (
    <Container className={styles['accordion__section-content']}>
      <Row>
        <Col xs={12} xl={8} className="mx-auto">
          <h4>{t(`${i18nPrefix}.intro.accordion.${index}.subtitle`)}</h4>
        </Col>
      </Row>
      <Row className={styles['accordion__section-comparison']}>
        <Col xs={12} xl={6} className={styles['accordion__section-col']}>
          <h5>{t(`${i18nPrefix}.intro.with-visalex`)}</h5>
          <p
            dangerouslySetInnerHTML={{
              // XSS Attack Prevention, pass only <strong>tags</strong>
              __html: t(`${i18nPrefix}.intro.accordion.${index}.pros-caption`).replace(
                /<(?!\/?b>|\/?strong>)[^>]+>/g,
                ''
              ),
            }}
          />
          <h6>{t(`${i18nPrefix}.intro.result`)}</h6>
          <ul className={styles.accordion__pros}>
            {pros.map((message) => (
              <li key={message}>
                <FontAwesomeIcon icon="check" />
                <span>{message}</span>
              </li>
            ))}
          </ul>
        </Col>
        <Col xs={12} xl={6} className={styles['accordion__section-col']}>
          <h5>{t(`${i18nPrefix}.intro.without-visalex`)}</h5>
          <p>{t(`${i18nPrefix}.intro.accordion.${index}.cons-caption`)}</p>
          <h6>{t(`${i18nPrefix}.intro.associated-risks`)}</h6>
          <ul className={styles.accordion__cons}>
            {cons.map((message) => (
              <li key={message}>
                <FontAwesomeIcon icon="exclamation-triangle" />
                {message}
              </li>
            ))}
          </ul>
        </Col>
      </Row>
    </Container>
  );
};

type mapType = {
  'employment-based': number[];
  'family-based': number[];
  'investment-based': number[];
};

enum mapKeys {
  employmentBased = 'employment-based',
  familyBased = 'family-based',
  investmentBased = 'investment-based',
}

const prosMap: mapType = {
  'employment-based': [1, 1, 1, 1, 1],
  'family-based': [1, 1, 1, 1, 1],
  'investment-based': [1, 3, 1, 1, 4],
};
const consMap: mapType = {
  'employment-based': [3, 4, 3, 3, 2],
  'family-based': [3, 4, 3, 3, 1],
  'investment-based': [4, 5, 3, 3, 2],
};
const prosAndConsConfig = (i18nPrefix: string) => {
  return [
    { index: 0, amountOfPros: prosMap[i18nPrefix as mapKeys][0], amountOfCons: consMap[i18nPrefix as mapKeys][0] },
    { index: 1, amountOfPros: prosMap[i18nPrefix as mapKeys][1], amountOfCons: consMap[i18nPrefix as mapKeys][1] },
    { index: 2, amountOfPros: prosMap[i18nPrefix as mapKeys][2], amountOfCons: consMap[i18nPrefix as mapKeys][2] },
    { index: 3, amountOfPros: prosMap[i18nPrefix as mapKeys][3], amountOfCons: consMap[i18nPrefix as mapKeys][3] },
    { index: 4, amountOfPros: prosMap[i18nPrefix as mapKeys][4], amountOfCons: consMap[i18nPrefix as mapKeys][4] },
  ];
};
const ProgressAccordion: FunctionComponent<I18nSupport> = ({ i18nPrefix }) => {
  const { t } = useTranslation();

  return (
    <Accordion
      allowMultipleOpen={false}
      className={styles.accordion}
      classNames={{
        accordion: styles.accordion,
        sectionHeader: styles.accordion__label,
        sectionWrapper: styles.accordion__section,
      }}
    >
      {prosAndConsConfig(i18nPrefix).map(({ index, ...rest }) => (
        <AccordionSection
          key={index}
          index={index}
          header={
            <AccordionHeader
              className={styles['accordion__label-content']}
              header={t(`${i18nPrefix}.intro.accordion.${index}.label`)}
              index={index}
            />
          }
          isOpen={index === 0}
        >
          <ProgressEntry key={index} index={index} t={t} i18nPrefix={i18nPrefix} {...rest} />
        </AccordionSection>
      ))}
    </Accordion>
  );
};

export default ProgressAccordion;
